'use client';

import { Box, CircularProgress } from '@mui/material';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import { extractFirstImgSrc } from '@/utils/stripHtmlForTooltip';

/** Inline = grid/name badges; detail = animal/member detail marketing logos. */
export type Ilr2ImgBadgeSize = 'inline' | 'detail';

const BADGE_SIZE_SX: Record<
  Ilr2ImgBadgeSize,
  { maxHeight: number; maxWidth: number | 'none'; minHeight: number }
> = {
  // Super Sire / completeness icons next to names in grids
  inline: { maxHeight: 24, maxWidth: 40, minHeight: 24 },
  // AD_Col marketing logos on detail pages (e.g. NZHA merial 180×55)
  detail: { maxHeight: 100, maxWidth: 'none', minHeight: 40 },
};

/** Renders img-only ILR2 markup (`<img src="/online/images/…">`) via the document proxy. */
export function Ilr2StructuredImgCell({
  value,
  societyId,
  alt = 'Badge',
  size = 'inline',
}: {
  value: string;
  societyId: string | number;
  alt?: string;
  size?: Ilr2ImgBadgeSize;
}) {
  const url = extractFirstImgSrc(value);
  const { imgSrc, isLoading, error } = useDocumentImageWithSociety(
    url,
    societyId,
  );
  const sizeSx = BADGE_SIZE_SX[size];

  if (!url) {
    return null;
  }

  if (error || (!isLoading && !imgSrc)) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: sizeSx.minHeight,
          minWidth: 24,
        }}
      >
        <CircularProgress size={size === 'detail' ? 20 : 16} />
      </Box>
    );
  }

  if (!imgSrc) {
    return null;
  }

  return (
    <Box
      component="img"
      src={imgSrc}
      alt={alt}
      sx={{
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        maxHeight: sizeSx.maxHeight,
        maxWidth: sizeSx.maxWidth,
        height: 'auto',
        width: 'auto',
      }}
    />
  );
}
